<script setup lang="ts">
import { ref, computed, watch, watchEffect, onMounted, toRef } from 'vue'

import { Icon } from '@iconify/vue'
import { useDebounceFn } from '@vueuse/core'

import CropperDialog from '@/components/frontend/shorter-list/dialog/CropperDialog.vue'
import UtmParamsForm from '@/components/frontend/shorter-list/form/UtmParamsForm.vue'
import ImageUploader from '@/components/frontend/shorter-list/shared/ImageUploader.vue'
import BaseButton from '@/components/UI/BaseButton.vue'

import { useFEUrlDialogMode } from '@/composables/useFEUrlDialogMode'

import { autoFocusFirstInput } from '@/utils/common/formHandler'
import { isUserUploadedImage } from '@/utils/file/isUserUploadedImage'
import { copyToClipboard } from '@/utils/ui/copyToClipboard'
import { getRadioColumns } from '@/utils/ui/getRadioColumns'
import { addQueryParamsToUrl, prepareOGImageInfo, getImageUpdateInfo } from '@/utils/url/shortUrlHelpers'

import * as aggr from '@/api-generator/aggr'
import { feShortUrlsAPI } from '@/APIs'
import { ORG_SORT_TAG_VARIANTS } from '@/constant/client/orgSortTag'
import { ShorterDialogMode } from '@/constant/client/shorterDialog'
import { tooltipText } from '@/constant/tooltip'
import { SHORT_URL_PATTERN, PHONE_NUMBER_PATTERN } from '@/constant/validation'
import appStore from '@/stores'

import type { UtmParamsFormModel, SingleUrlSubmitData, ShorterSingleFormModel, GroupData } from '@/types/shorterUrlForm'
import type { FormInstance, FormRules, UploadFile } from 'element-plus'

const shortUrlStore = appStore.useShortUrlStore
const orgSortTagStore = appStore.useOrgSortTagStore

interface Props {
  mode: ShorterDialogMode
  groupData?: GroupData // 群組內建立時需要的群組資訊
  onSubmit: (data: SingleUrlSubmitData) => void | Promise<void>
  onCancel: () => void
}

const props = defineProps<Props>()

const formData = defineModel<ShorterSingleFormModel>({ default: () => ({}) })

const {
  isSingleEditMode,
  isSingleUpdateMode,
  isSingleCreateOrEditMode,
  isCreatingUrlInGroup,
  isTelUrlDisabled,
  showSubtagSelection,
  isSubtagDisabled,
  buttonTexts
} = useFEUrlDialogMode(toRef(props, 'mode'))

// ==================== Form State ====================
const formRef = ref<FormInstance | null>(null)
const firstInput = ref<HTMLInputElement | null>(null)
const isLoading = ref(false)

// UTM 參數
const utmParams = ref<UtmParamsFormModel>({
  campaignSource: '',
  campaignMedium: '',
  campaignName: '',
  campaignTerm: '',
  campaignContent: ''
})

// OG Meta 相關
const autoFetchOgInfo = ref(true)
const showOgInfo = ref(false)
const showUtmTooltip = ref(false)
const newOgImageUrl = ref('') // 編輯模式下的新圖片

// 電話號碼 OG Title 同步
const isOgTitleManuallyModified = ref(false)
const shouldSyncOgTitle = ref(true)

// 圖片裁切
const selectedImage = ref('')
const cropperDialogVisible = ref(false)
const croppedImage = ref<string | null>(null)
const imgLoading = ref(false)

// 短網址（編輯模式）
const urlCode = ref('')

// 2025/04/28 暫時隱藏 Tape ID 開關
const showTraceTapeIdSwitch = ref(false)

const isTelUrlCreating = computed(() => formData.value.urlType === aggr.UrlType.Tel)

const chineseTagTooltipText = computed(() => {
  return isSingleEditMode.value ? tooltipText.nonEditableChineseTag : tooltipText.chineseTag
})

// 超過 10 次點擊後禁止修改中文標記
const isChineseTagInputDisabled = computed(() => {
  if (formData.value.shortURLTotalClicks) {
    return formData.value.shortURLTotalClicks > 10
  }
  return false
})

const submitButtonText = computed(() => {
  const texts = buttonTexts.value
  return isLoading.value ? texts.loadingBtnText : texts.defaultBtnText
})

interface ValidateCallback {
  (error?: Error): void
}

const validateOriginalUrl = (_rule: unknown, value: string, callback: ValidateCallback) => {
  if (formData.value.urlType === aggr.UrlType.Url) {
    if (!value) {
      callback(new Error('請輸入原始網址'))
    } else if (!SHORT_URL_PATTERN.test(value)) {
      callback(new Error('請輸入有效的URL'))
    } else {
      callback()
    }
  } else if (formData.value.urlType === aggr.UrlType.Tel) {
    if (!value) {
      callback(new Error('請輸入電話號碼'))
    } else if (!PHONE_NUMBER_PATTERN.test(value)) {
      callback(new Error('請輸入正確的電話號碼'))
    } else {
      callback()
    }
  } else {
    callback()
  }
}

const formRules: FormRules<ShorterSingleFormModel> = {
  projectName: [{ required: true, message: '請輸入專案名稱', trigger: ['blur', 'change'] }],
  subtagId: [{ required: true, message: '請選擇一個受眾標籤', trigger: 'change' }],
  originalUrl: [{ validator: validateOriginalUrl, trigger: ['blur', 'change'] }]
}

/**
 * 重置 UTM 參數
 */
const resetUtmParams = () => {
  utmParams.value = {
    campaignSource: '',
    campaignMedium: '',
    campaignName: '',
    campaignTerm: '',
    campaignContent: ''
  }
}

/**
 * 重置 OG 資訊
 */
const resetOgInfo = () => {
  formData.value.title = ''
  formData.value.description = ''
  formData.value.imageUrl = ''
  formData.value.uploadImageBase64 = ''
  croppedImage.value = ''
}

/**
 * 獲取 OG Meta 資訊
 */
const handleOriginalUrlBlur = async () => {
  if (!formData.value.originalUrl || isTelUrlCreating.value || !autoFetchOgInfo.value) {
    return
  }
  const reqBody: aggr.ShortUrlOgMetaRq = {
    url: formData.value.originalUrl
  }

  try {
    const { data } = await feShortUrlsAPI.queryOgMeta(reqBody)
    const { title: ogTitle, description: ogDesc, image } = data

    formData.value.title = ogTitle ?? ''
    formData.value.description = ogDesc ?? ''
    formData.value.imageUrl = image ?? ''
    croppedImage.value = image ?? ''

    // 編輯模式：檢查是否有新圖片
    if (isSingleUpdateMode.value && checkIsNewOgImage(image || '')) {
      newOgImageUrl.value = image || ''
    }
  } catch (error) {
    console.error('獲取 OG Meta 失敗:', error)
  }
}

const handleInputDebounced = useDebounceFn(() => {
  handleOriginalUrlBlur()
}, 800)

/**
 * 檢查是否有新的 OG Image
 */
const checkIsNewOgImage = (ogImageUrl: string): boolean => {
  // 編輯模式下，比對新舊圖片 URL
  if (!formData.value.thumbnailImageUrl) {
    return false
  }
  return ogImageUrl !== formData.value.thumbnailImageUrl
}

/**
 * 電話號碼 OG Title 狀態更新
 */
const updateOgTitleStatus = (ogTitle: string, url: string) => {
  if (!ogTitle.trim() || !url.trim()) {
    return
  }

  const currentDefaultTitle = `Talk to us ${url}`
  if (ogTitle !== currentDefaultTitle) {
    isOgTitleManuallyModified.value = true
    shouldSyncOgTitle.value = false
  } else {
    isOgTitleManuallyModified.value = false
    shouldSyncOgTitle.value = true
  }
}

/**
 * 圖片上傳處理
 */
const handleUploadImage = (file: UploadFile): void => {
  if (!file.raw) {
    return
  }

  const reader = new FileReader()
  reader.onload = (e) => {
    if (e.target && e.target.result) {
      selectedImage.value = e.target.result as string
      cropperDialogVisible.value = true
    }
  }
  reader.readAsDataURL(file.raw)
}

/**
 * 圖片上傳錯誤處理
 */
const handleUploadError = (message: string) => {
  console.error('圖片上傳錯誤:', message)
}

const handleUpdateCroppedImage = (imgDataUrl: string) => {
  croppedImage.value = imgDataUrl
}

const handleUpdateCroppedImageBase64 = (base64Data: string) => {
  formData.value.uploadImageBase64 = base64Data
}

/**
 * 複製短網址
 */
const copyURLCode = async () => {
  await copyToClipboard(formData.value.urlCode, {
    successMessage: '短網址已複製',
    errorMessage: '複製失敗'
  })
}

const copyUTMContent = async () => {
  await copyToClipboard(formData.value.directUrl || '', {
    successMessage: 'UTM URL 已複製',
    errorMessage: '複製失敗'
  })
}

const toggleOgInfo = () => {
  showOgInfo.value = !showOgInfo.value
}

const toggleTooltip = () => {
  showUtmTooltip.value = !showUtmTooltip.value
}

/**
 * URL 類型變化處理
 */
const handleUrlTypeChange = (newVal: aggr.UrlType) => {
  formRef.value?.clearValidate('originalUrl')
  if (newVal === aggr.UrlType.Tel) {
    resetUtmParams()
    resetOgInfo()
    autoFetchOgInfo.value = false
  } else {
    autoFetchOgInfo.value = true
  }
}

/**
 * 自動抓取 OG 資訊開關變化處理
 */
const handleAutoFetchOgInfoChange = (newVal: string | number | boolean) => {
  if (newVal === true) {
    handleOriginalUrlBlur()
  }
}

/**
 * 原始網址輸入處理
 */
const handleOriginalUrlInput = () => {
  // 建立模式: 換網址時先清掉舊的 OG 縮圖/標題, 避免和新網址不同步
  // 自動抓取開啟時, 下方 debounce 會重抓覆寫; 關閉時就維持清空, 不會殘留上一個網址的縮圖
  // 編輯模式不處理: 後端無法清空既有 UrlImageUrl, 維持原圖行為
  if (!autoFetchOgInfo.value && !isTelUrlCreating.value && !isSingleUpdateMode.value) {
    resetOgInfo()
  }

  handleInputDebounced()

  // 電話號碼標題同步
  if (isTelUrlCreating.value && isSingleCreateOrEditMode.value) {
    if (shouldSyncOgTitle.value && !isOgTitleManuallyModified.value) {
      formData.value.title = `Talk to us ${formData.value.originalUrl}`
    }
  }
}

/**
 * 標題輸入處理
 */
const handleTitleInput = () => {
  if (isTelUrlCreating.value && isSingleCreateOrEditMode.value) {
    updateOgTitleStatus(formData.value.title || '', formData.value.originalUrl || '')
  }
}

/**
 * 準備提交資料
 */
const prepareSubmitData = (): SingleUrlSubmitData => {
  let imageData: Pick<SingleUrlSubmitData, 'imageUrl' | 'uploadImageBase64'> &
    Partial<Pick<SingleUrlSubmitData, 'newImageUrl' | 'newUploadImageBase64' | 'hasUpdateImage'>> = prepareOGImageInfo({
    autoFetchOgInfo: autoFetchOgInfo.value,
    imageUrl: formData.value.imageUrl || '',
    uploadImageBase64: formData.value.uploadImageBase64 || ''
  })

  // 編輯模式：使用圖片更新資訊
  if (isSingleUpdateMode.value) {
    const updateInfo = getImageUpdateInfo({
      autoFetchOgInfo: autoFetchOgInfo.value,
      newOgImageUrl: newOgImageUrl.value,
      uploadImageBase64: formData.value.uploadImageBase64 || ''
    })
    imageData = {
      imageUrl: '',
      uploadImageBase64: '',
      ...updateInfo
    }
  }

  // 電話號碼模式：directUrl 使用 originalUrl
  const finalDirectUrl = isTelUrlCreating.value ? formData.value.originalUrl : formData.value.directUrl

  return {
    projectName: formData.value.projectName || '',
    subtagId: Number(formData.value.subtagId),
    originalUrl: formData.value.originalUrl || '',
    urlType: formData.value.urlType || aggr.UrlType.Url,
    directUrl: finalDirectUrl || '',
    utm: { ...utmParams.value },
    chineseTag: formData.value.chineseTag || null,
    title: formData.value.title || null,
    description: formData.value.description || null,
    enableTraceTapeId: formData.value.enableTraceTapeId ?? true,
    orgTagId: formData.value.orgTagId ?? undefined,
    ...imageData
  }
}

/**
 * 提交表單
 */
const submitForm = () => {
  if (!formRef.value) {
    return
  }

  formRef.value.validate(async (valid) => {
    if (!valid) {
      console.error('表單驗證失敗')
      return
    }

    isLoading.value = true
    try {
      const submitData = prepareSubmitData()
      await props.onSubmit(submitData)
    } catch (error) {
      console.error('提交失敗:', error)
    } finally {
      isLoading.value = false
    }
  })
}

/**
 * 取消
 */
const handleCancel = () => {
  props.onCancel()
}

/**
 * 切換分類標記選擇
 * 點選相同的標記時取消選擇
 */
const toggleOrgTag = (tagId: number | undefined) => {
  if (!tagId) {
    return
  }

  if (formData.value.orgTagId === tagId) {
    formData.value.orgTagId = null
  } else {
    formData.value.orgTagId = tagId
  }
}

// ==================== Watchers ====================

watch(
  () => formData.value.utm,
  (newUtm) => {
    utmParams.value = {
      campaignSource: newUtm?.campaignSource || '',
      campaignMedium: newUtm?.campaignMedium || '',
      campaignName: newUtm?.campaignName || '',
      campaignTerm: newUtm?.campaignTerm || '',
      campaignContent: newUtm?.campaignContent || ''
    }
  },
  { immediate: true }
)

// 監聽 formData 的圖片變化，同步到 croppedImage
watch(
  () => formData.value.imageUrl,
  (newImageUrl) => {
    if (newImageUrl && !croppedImage.value) {
      // 只在 croppedImage 為空時才設置（避免覆蓋用戶上傳的圖片）
      croppedImage.value = newImageUrl
    }
  },
  { immediate: true }
)

// 監聽 thumbnailImageUrl 的變化（編輯模式）
watch(
  () => formData.value.thumbnailImageUrl,
  (newThumbnailUrl) => {
    if (newThumbnailUrl && !croppedImage.value) {
      croppedImage.value = newThumbnailUrl
      // 同時設置 urlCode（編輯模式下的短網址）
      if (formData.value.urlCode) {
        urlCode.value = formData.value.urlCode
      }
    }

    // 編輯模式：根據圖片類型設置 autoFetchOgInfo
    if (isSingleUpdateMode.value && newThumbnailUrl) {
      // 如果是電話類型或用戶上傳的圖片，關閉自動抓取
      if (formData.value.urlType === aggr.UrlType.Tel || isUserUploadedImage(newThumbnailUrl)) {
        autoFetchOgInfo.value = false
      }
    }
  },
  { immediate: true }
)

// 監聽圖片變化
watch(croppedImage, (newVal) => {
  if (newVal) {
    const image = new Image()
    image.src = newVal
    imgLoading.value = true

    image.onload = () => {
      imgLoading.value = false
    }

    image.onerror = () => {
      imgLoading.value = false
      console.error('圖片加載失敗')
    }
  }
})

// 自動生成 directUrl
watchEffect(() => {
  if (isTelUrlCreating.value) {
    return
  }

  const params = {
    utm_source: utmParams.value.campaignSource,
    utm_medium: utmParams.value.campaignMedium,
    utm_campaign: utmParams.value.campaignName,
    utm_term: utmParams.value.campaignTerm,
    utm_content: utmParams.value.campaignContent
  }

  const filteredParams = Object.fromEntries(Object.entries(params).filter(([, value]) => value?.trim()))

  formData.value.directUrl = addQueryParamsToUrl(formData.value.originalUrl || '', filteredParams)
})

/** 群組內操作的模式 */
const inGroupModes: ShorterDialogMode[] = [
  ShorterDialogMode.SINGLE_CREATE_IN_GROUP,
  ShorterDialogMode.SINGLE_EDIT_IN_GROUP,
  ShorterDialogMode.SINGLE_COPY_IN_GROUP
]
const showOrgSortTags = computed(() => !inGroupModes.includes(props.mode))

const radioColumns = computed(() => getRadioColumns(shortUrlStore.subTags))

onMounted(async () => {
  await autoFocusFirstInput(firstInput.value)
})
</script>

<template>
  <el-form ref="formRef" class="shorter-url-form" :validate-on-rule-change="true" :rules="formRules" :model="formData">
    <!-- 專案名稱 -->
    <el-form-item class="form-title" prop="projectName">
      <span class="shorter-url-form-label mb-2">
        <template v-if="isCreatingUrlInGroup">子專案名稱</template>
        <template v-else> 專案名稱</template>
      </span>
      <el-input ref="firstInput" v-model="formData.projectName" autocomplete="off" />
    </el-form-item>

    <!-- 受眾標籤 -->
    <el-form-item v-if="showSubtagSelection" class="form-title" prop="subtagId">
      <span class="shorter-url-form-label mb-2">受眾標籤</span>
      <el-radio-group
        v-model="formData.subtagId"
        class="radio-group"
        :style="{
          display: 'grid',
          gridTemplateColumns: `repeat(${radioColumns}, 1fr)`
        }"
      >
        <el-radio
          v-for="subTag in shortUrlStore.subTags"
          :key="subTag.subtagId"
          :value="subTag.subtagId"
          :label="subTag.subtagId"
          :disabled="isSubtagDisabled"
        >
          <span class="text-sm font-normal"> {{ subTag.subtagName }}</span>
        </el-radio>
      </el-radio-group>
    </el-form-item>

    <!-- 原始網址 -->
    <el-form-item prop="originalUrl" style="margin-bottom: 24px !important">
      <span class="shorter-url-form-label mb-3">原始網址</span>
      <template v-if="isTelUrlDisabled">
        <el-input v-model="formData.originalUrl" autocomplete="off" @input="handleOriginalUrlInput" />
      </template>
      <template v-else>
        <el-input
          v-model="formData.originalUrl"
          autocomplete="off"
          class="original-url-input"
          @input="handleOriginalUrlInput"
        >
          <template #prepend>
            <el-select
              v-model="formData.urlType"
              style="width: 80px; background-color: #ffffff"
              :disabled="isSingleEditMode"
              @change="handleUrlTypeChange"
            >
              <el-option label="網址" :value="aggr.UrlType.Url" />
              <el-option label="電話" :value="aggr.UrlType.Tel" />
            </el-select>
          </template>
        </el-input>
      </template>
    </el-form-item>
    <div class="dividing-line" :class="isCreatingUrlInGroup ? '-mx-4' : '-mx-6'"></div>
    <!-- UTM 參數 -->
    <div class="py-4">
      <div class="flex cursor-pointer items-center justify-between" @click="toggleTooltip">
        <div class="flex items-center gap-1">
          <span class="shorter-url-form-label">UTM 參數標記</span>
          <span>
            <el-tooltip :placement="'top-start'" :content="tooltipText.utmParamsContent">
              <Icon icon="ri:information-fill" width="14px" height="14px" class="text-app-black-300" />
            </el-tooltip>
          </span>
        </div>
        <Icon
          icon="ri:arrow-down-s-line"
          width="14px"
          height="14px"
          class="text-app-brand-blue-500 transition-transform duration-300 ease-in-out"
          :class="{ 'rotate-180': showUtmTooltip }"
        />
      </div>
      <div v-show="showUtmTooltip" class="pt-2">
        <UtmParamsForm v-model="utmParams" :is-tel-url-creating="isTelUrlCreating" />
        <div v-if="showTraceTapeIdSwitch" class="traceTapeId">
          <el-switch v-model="formData.enableTraceTapeId" size="small" class="ml-2" />
          <div class="explain">自動追蹤轉換，提升短網址成效</div>
        </div>
        <div class="mt-2">
          <span class="shorter-url-form-label">預覽網址</span>
          <div
            class="relative mt-2 min-h-[72px] cursor-pointer rounded-lg border border-solid border-app-black-100 px-3 py-2 text-app-black-600"
            @click="copyUTMContent"
          >
            {{ formData.directUrl }}
            <span class="absolute bottom-2.5 right-2.5">
              <Icon icon="ri:file-copy-line" width="14px" height="14px" class="text-app-black-500" />
            </span>
          </div>
        </div>
      </div>
    </div>
    <div class="dividing-line" :class="isCreatingUrlInGroup ? '-mx-4' : '-mx-6'"></div>

    <!-- 預覽設定 -->
    <div class="py-4">
      <div class="flex w-full cursor-pointer items-center justify-between" @click="toggleOgInfo">
        <span class="shorter-url-form-label">自訂預覽縮圖</span>
        <Icon
          icon="ri:arrow-down-s-line"
          width="14px"
          height="14px"
          class="text-app-brand-blue-500 transition-transform duration-300 ease-in-out"
          :class="{ 'rotate-180': showOgInfo }"
        />
      </div>
      <div v-show="showOgInfo" class="w-full pt-4">
        <div class="mb-2.5 flex items-center justify-between">
          <span class="text-sm text-app-black-600">自動抓取網址縮圖及資訊 (取消可自訂縮圖、標題描述)</span>
          <el-switch
            v-model="autoFetchOgInfo"
            size="small"
            :disabled="isTelUrlCreating"
            style="--el-switch-on-color: #1c7af3"
            @change="handleAutoFetchOgInfoChange"
          />
        </div>
        <!-- 圖片上傳 -->
        <div class="mb-4">
          <ImageUploader
            :cropped-image="croppedImage"
            :auto-fetch-enabled="autoFetchOgInfo"
            :disabled="autoFetchOgInfo"
            :loading="imgLoading"
            :tooltip-text="tooltipText.imageRecommendation"
            upload-style="detailed"
            @change="handleUploadImage"
            @error="handleUploadError"
          />
        </div>
        <!-- OG Title -->
        <el-form-item class="og-info-label">
          標題
          <el-input
            v-model="formData.title"
            :disabled="autoFetchOgInfo"
            autocomplete="off"
            class="og-info-input"
            @input="handleTitleInput"
          />
        </el-form-item>

        <!-- OG Description -->
        <el-form-item class="og-info-label">
          描述
          <el-input
            v-model="formData.description"
            :disabled="autoFetchOgInfo"
            autocomplete="off"
            class="og-info-input"
          />
        </el-form-item>
      </div>
    </div>
    <div class="dividing-line" :class="isCreatingUrlInGroup ? '-mx-4' : '-mx-6'"></div>

    <!-- 自訂網址後綴名稱 -->
    <div class="pb-6 pt-6">
      <div class="mb-2 flex items-center gap-1">
        <span class="shorter-url-form-label">自訂網址後綴名稱</span>
        <el-tooltip :show-after="100" :content="chineseTagTooltipText" placement="top-start">
          <Icon icon="ri:information-fill" width="14px" height="14px" class="text-app-black-300" />
        </el-tooltip>
      </div>
      <el-input v-model="formData.chineseTag" autocomplete="off" :disabled="isChineseTagInputDisabled" />
    </div>
    <div v-if="showOrgSortTags" class="pb-6">
      <span class="shorter-url-form-label">分類標記</span>
      <ul
        v-if="orgSortTagStore.orgSortTags && orgSortTagStore.orgSortTags.length > 0"
        class="mt-3 flex flex-wrap items-center gap-2.5"
      >
        <li v-for="tag in orgSortTagStore.orgSortTags" :key="tag.orgTagId" @click="toggleOrgTag(tag.orgTagId)">
          <OrgSortTag
            :org-tag-id="tag.orgTagId"
            :variant="ORG_SORT_TAG_VARIANTS.TAG"
            :selected="formData.orgTagId === tag.orgTagId"
          />
        </li>
      </ul>
      <div v-else class="flex justify-center py-3 text-app-black-600">尚無分類標記</div>
    </div>

    <!-- 短網址（編輯模式） -->
    <div v-if="isSingleEditMode" class="pb-6">
      <span class="shorter-url-form-label">短網址</span>
      <el-input v-model="formData.urlCode" class="short-url-copy-input mt-3" autocomplete="off" :disabled="true">
        <template #append>
          <button
            type="button"
            class="block h-full rounded-r-lg px-[18px] text-sm font-normal text-app-black-600"
            @click="copyURLCode"
          >
            複製
          </button>
        </template>
      </el-input>
    </div>

    <!-- 按鈕 -->
    <div class="mt-7 flex items-center justify-between gap-3">
      <BaseButton class="short-url-dialog-btn w-1/2 border-app-black-100 bg-white" @click="handleCancel">
        取消
      </BaseButton>
      <BaseButton
        class="short-url-dialog-btn w-1/2 border-app-brand-blue-500 bg-app-brand-blue-500 text-white hover:bg-app-brand-blue-500/90"
        :loading="isLoading"
        @click="submitForm"
      >
        {{ submitButtonText }}
      </BaseButton>
    </div>
  </el-form>

  <!-- 圖片裁切彈窗 -->
  <CropperDialog
    v-model="cropperDialogVisible"
    :selected-image="selectedImage"
    @update:cropped-image="handleUpdateCroppedImage"
    @update:cropped-image-base64="handleUpdateCroppedImageBase64"
  />
</template>

<style lang="scss" scoped>
:deep(.el-form-item__content) {
  color: var(--primary-1);
  font-size: 13px;
  letter-spacing: 1.147px;
}

.form-title {
  width: 100%;
  display: flex;
  flex-direction: column;
  margin-bottom: 25px;
  .radio-group {
    width: 100%;
  }

  .icon {
    margin-left: 4px;
    font-size: 12px;
  }

  .utm-url-content {
    word-break: break-all;
    width: 100%;
    line-height: 1.2;
    margin-bottom: 8px;
    font-size: 12px;
    color: var(--gray-8c);
  }
}

.og-info-label {
  margin-bottom: 20px;
}

.copy-icon {
  font-size: 12px;
  margin-left: 4px;
  cursor: pointer;
}

:deep(.el-upload-dragger) .el-upload__text {
  font-size: 13px;
  color: var(--gray-8c);
}

.dialog-footer {
  display: flex;
  place-content: center;
  margin-top: 40px;
  .btn {
    width: 138px;
    height: 36px;
  }
}

.traceTapeId {
  display: flex;
  align-items: center;
  .explain {
    margin-left: 8px;
    padding-bottom: 2px;
    font-size: 12px;
    color: var(--gray-8c);
  }
}

:deep(.el-input__inner) {
  font-size: 12px;
  color: var(--gray-9a);
}

:deep(.el-input-group__append) {
  background-color: #ffffff;
}

:deep(.el-input__validateIcon) {
  display: none;
}

.dividing-line {
  @apply h-px bg-app-black-50;
}
</style>
